import React, { createContext, useContext, useState } from 'react';

interface ApiSettings {
  clientId: string;
  clientSecret: string;
  environment: string;
  gatewayApiUrl: string;
  builderApiUrl: string;
  tokenSegment: string;
  selectedModelId?: string;
  selectedModelLabel?: string;
  token?: string;
  tokenExpiry?: number;
  prismaticApiUrl?: string;
  prismaticApiKey?: string;
}

interface Settings {
  selectedCapability: string;
}

interface SettingsContextType {
  apiSettings: ApiSettings;
  updateApiSettings: (settings: Partial<ApiSettings>) => void;
  saveSettings: () => void;
  isConfigured: boolean;
  clearSettings: () => void;
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
  showModelSelector: boolean;
  setShowModelSelector: (show: boolean) => void;
  selectModel: (id: string, label: string) => void;
}

const defaultSettings: ApiSettings = {
  clientId: '',
  clientSecret: '',
  environment: 'research',
  gatewayApiUrl: 'http://gateway-devops.capsifi.com',
  builderApiUrl: 'https://research.capsifi.com',
  tokenSegment: 'connect/token',
  prismaticApiUrl: 'https://hooks.eu-west-2.prismatic.io/trigger/SW5zdGFuY2VGbG93Q29uZmlnOmU0ZmZkZjIwLWMyOGUtNDk0Ny05YTVjLWU3MjFlNjAwMmE5Zg==',
  prismaticApiKey: ''
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [apiSettings, setApiSettings] = useState<ApiSettings>(() => {
    const savedSettings = sessionStorage.getItem('apiSettings');
    const parsedSettings = savedSettings ? JSON.parse(savedSettings) : defaultSettings;

    // Load Prismatic credentials from localStorage
    try {
      const prismaticApiUrl = localStorage.getItem('prismaticApiUrl');
      const prismaticApiKey = localStorage.getItem('prismaticApiKey');

      if (prismaticApiUrl || prismaticApiKey) {
        return {
          ...parsedSettings,
          prismaticApiUrl: prismaticApiUrl || parsedSettings.prismaticApiUrl,
          prismaticApiKey: prismaticApiKey || parsedSettings.prismaticApiKey
        };
      }
    } catch (e) {
      console.error('Failed to load Prismatic credentials from localStorage:', e);
    }

    return parsedSettings;
  });

  const [showModelSelector, setShowModelSelector] = useState(false);

  const [settings, setSettings] = useState<Settings>(() => ({
    selectedCapability: ''
  }));

  const updateApiSettings = (settings: Partial<ApiSettings>) => {
    setApiSettings(prev => {
      const newSettings = { ...prev, ...settings };

      // Save Prismatic credentials to localStorage if they're being updated
      if (settings.prismaticApiUrl !== undefined) {
        try {
          localStorage.setItem('prismaticApiUrl', settings.prismaticApiUrl || '');
        } catch (e) {
          console.error('Failed to save Prismatic API URL to localStorage:', e);
        }
      }

      if (settings.prismaticApiKey !== undefined) {
        try {
          localStorage.setItem('prismaticApiKey', settings.prismaticApiKey || '');
        } catch (e) {
          console.error('Failed to save Prismatic API Key to localStorage:', e);
        }
      }

      return newSettings;
    });
  };

  const saveSettings = () => {
    const { prismaticApiUrl, prismaticApiKey, ...sessionSettings } = apiSettings;
    sessionStorage.setItem('apiSettings', JSON.stringify(sessionSettings));
  };

  const clearSettings = () => {
    setApiSettings(defaultSettings);
    sessionStorage.removeItem('apiSettings');
    // Also clear Prismatic credentials from localStorage
    localStorage.removeItem('prismaticApiUrl');
    localStorage.removeItem('prismaticApiKey');
  };

  const selectModel = (id: string, label: string) => {
    setApiSettings(prev => {
      const next = { ...prev, selectedModelId: id, selectedModelLabel: label };
      const { prismaticApiUrl, prismaticApiKey, ...sessionSettings } = next;
      sessionStorage.setItem('apiSettings', JSON.stringify(sessionSettings));
      return next;
    });
    setShowModelSelector(false);
  };

  // The user authenticates via OIDC (enforced by AppLayout), and that token is
  // forwarded as the Bearer on every Capsifi call. "Configured" therefore only
  // requires that a model has been selected for the session.
  const isConfigured = Boolean(apiSettings.selectedModelId);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  return (
    <SettingsContext.Provider
      value={{
        apiSettings,
        updateApiSettings,
        saveSettings,
        isConfigured,
        clearSettings,
        settings,
        updateSettings,
        showModelSelector,
        setShowModelSelector,
        selectModel
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
} 